import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { Bot, BrainCircuit, Cloud, Code2, Database, Eye, File as FileIcon, FileText, History, Layers, MessageSquareText, MousePointer, Palette, Plus, Redo2, Rocket, Save, Share2, Shield, Sparkles, Undo2, Users, Wand2, Workflow, X } from "lucide-react";
import * as React from "react";

import { AiPanel } from "@/components/ai-panel/ai-panel";
import { AgentPanel } from "@/components/builder/agent-panel";
import { AuditLogPanel } from "@/components/builder/audit-log-panel";
import { BrandPackSelector } from "@/components/builder/brand-pack-selector";
import { BuilderCanvas, CANVAS_DROP_ZONE_ID } from "@/components/builder/builder-canvas";
import { ContentViewPanel } from "@/components/builder/content-view-panel";
import { DataViewPanel } from "@/components/builder/data-view-panel";
import { DecrossPublishControl } from "@/components/builder/decross-publish-control";
import { DeploymentPanel } from "@/components/builder/deployment-panel";
import { KnowledgePanel } from "@/components/builder/knowledge-panel";
import { ComponentPanel } from "@/components/builder/component-panel";
import { FileManager } from "@/components/builder/file-manager";
import { LayersPanel } from "@/components/builder/layers-panel";
import { NlCanvas } from "@/components/builder/nl-canvas";
import { PageRenderer } from "@/components/builder/runtime-renderer";
import { PreviewDialog } from "@/components/builder/preview-dialog";
import { PropertyPanel } from "@/components/builder/property-panel";
import { VersionHistoryDialog } from "@/components/builder/version-history-dialog";
import { WorkflowPanel } from "@/components/builder/workflow-panel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSearchParams } from "react-router-dom";

import { useCollaboration } from "@/hooks/use-collaboration";
import { generateApp } from "@/services/ai";
import { createApp, updateApp, publishApp, applyDataModels, listApps, exportApp, type AppRecord } from "@/services/apps";
import type { DebugSchemaDetail } from "@/services/debugpavilion";
import { useBuilderStore } from "@/lib/builder/builder-store";
import { applyBrandPackToTheme, loadBrandPacks } from "@/lib/builder/brand-pack-connect";
import { getMaterial } from "@/lib/builder/materials";
import { createDefaultApp } from "@/lib/builder/schema";
import { downloadProjectZip } from "@/lib/builder/code-export";
import { appTemplates } from "@/lib/builder/templates";
import { v4 as uuidv4 } from "uuid";

import { throttle, cn } from "@/lib/utils";
import type { AppSchema, BrandPack, FieldType } from "@shared/types";

const fieldTypeOptions: { value: FieldType; label: string }[] = [
  { value: "text", label: "短文本" },
  { value: "longText", label: "长文本" },
  { value: "integer", label: "整数" },
  { value: "number", label: "小数" },
  { value: "boolean", label: "布尔" },
  { value: "datetime", label: "日期时间" },
  { value: "date", label: "日期" },
  { value: "uuid", label: "UUID" },
  { value: "json", label: "JSON" },
  { value: "reference", label: "关联" },
  { value: "email", label: "邮箱" },
  { value: "url", label: "URL" },
  { value: "file", label: "文件" },
];

function PageTabs() {
  const { schema, activePageId, setActivePage, addPage, updatePage, deletePage } = useBuilderStore();
  const canDeletePage = schema.pages.length > 1;
  return (
    <div className="flex items-center gap-2 border-b bg-card px-3 py-2">
      {schema.pages.map((page) =>
        activePageId === page.id ? (
          <div key={page.id} className="flex items-center gap-1">
            <Input
              value={page.name}
              onChange={(e) => updatePage(page.id, { name: e.target.value })}
              className="h-7 w-32 text-xs"
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive"
              disabled={!canDeletePage}
              onClick={() => deletePage(page.id)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <div key={page.id} className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setActivePage(page.id)}
            >
              {page.name}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive"
              disabled={!canDeletePage}
              onClick={() => deletePage(page.id)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ),
      )}
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={addPage}>
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
}

function DataModelPanel() {
  const { schema, addDataModel, updateDataModel, deleteDataModel } = useBuilderStore();
  const [applying, setApplying] = React.useState(false);
  const [applyResult, setApplyResult] = React.useState<{ applied: string[]; errors: string[] } | null>(null);

  const handleApply = async () => {
    setApplying(true);
    try {
      const result = await applyDataModels(schema.id, schema.dataModels);
      setApplyResult(result);
    } catch (error) {
      setApplyResult({ applied: [], errors: [String(error)] });
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">数据模型</h3>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={addDataModel}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            新建表
          </Button>
          <Button size="sm" variant="outline" disabled={applying || schema.dataModels.length === 0} onClick={handleApply}>
            {applying ? "同步中..." : "同步到 Supabase"}
          </Button>
        </div>
      </div>
      {applyResult && (
        <div className="rounded-md border bg-card p-3 text-sm">
          {applyResult.applied.length > 0 && (
            <p className="text-green-600">已应用：{applyResult.applied.join(", ")}</p>
          )}
          {applyResult.errors.length > 0 && (
            <p className="text-destructive">错误：{applyResult.errors.join("; ")}</p>
          )}
        </div>
      )}
      {schema.dataModels.length === 0 && (
        <p className="text-sm text-muted-foreground">暂无数据表，点击「新建表」开始。</p>
      )}
      <div className="grid gap-4 md:grid-cols-2">
        {schema.dataModels.map((model) => (
          <Card key={model.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Input
                  value={model.name}
                  onChange={(e) => updateDataModel(model.id, { name: e.target.value })}
                  className="h-8 text-sm font-semibold"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  onClick={() => deleteDataModel(model.id)}
                >
                  删除
                </Button>
              </div>
              <Input
                value={model.tableName}
                onChange={(e) => updateDataModel(model.id, { tableName: e.target.value })}
                className="h-7 text-xs text-muted-foreground"
                placeholder="表名"
              />
            </CardHeader>
            <CardContent className="space-y-2">
              {model.fields.map((field) => (
                <div key={field.id} className="flex items-center gap-2">
                  <Input
                    value={field.name}
                    onChange={(e) =>
                      updateDataModel(model.id, {
                        fields: model.fields.map((f) => (f.id === field.id ? { ...f, name: e.target.value } : f)),
                      })
                    }
                    className="h-8 text-xs"
                    placeholder="字段名"
                  />
                  <select
                    value={field.type}
                    onChange={(e) =>
                      updateDataModel(model.id, {
                        fields: model.fields.map((f) =>
                          f.id === field.id ? { ...f, type: e.target.value as FieldType } : f,
                        ),
                      })
                    }
                    className="h-8 rounded-md border bg-background px-2 text-xs"
                  >
                    {fieldTypeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <label className="flex items-center gap-1 text-xs">
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) =>
                        updateDataModel(model.id, {
                          fields: model.fields.map((f) =>
                            f.id === field.id ? { ...f, required: e.target.checked } : f,
                          ),
                        })
                      }
                    />
                    必填
                  </label>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() =>
                  updateDataModel(model.id, {
                    fields: [...model.fields, { id: uuidv4(), name: "new_field", type: "text" }],
                  })
                }
              >
                添加字段
              </Button>
              <div className="pt-2">
                <Textarea
                  value={generateCreateTableSQL(model.tableName, model.fields)}
                  readOnly
                  rows={4}
                  className="font-mono text-xs"
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function generateCreateTableSQL(tableName: string, fields: { name: string; type: FieldType; required?: boolean; primaryKey?: boolean }[]) {
  const typeMap: Record<FieldType, string> = {
    text: "TEXT",
    longText: "TEXT",
    integer: "INTEGER",
    number: "NUMERIC",
    boolean: "BOOLEAN",
    datetime: "TIMESTAMPTZ",
    date: "DATE",
    uuid: "UUID",
    json: "JSONB",
    reference: "UUID",
    email: "TEXT",
    url: "TEXT",
    file: "TEXT",
  };
  const lines = fields.map((f) => {
    const pgType = typeMap[f.type] || "TEXT";
    const constraints = [f.primaryKey ? "PRIMARY KEY" : "", f.required ? "NOT NULL" : ""].filter(Boolean).join(" ");
    return `  "${f.name}" ${pgType}${constraints ? ` ${constraints}` : ""}`;
  });
  lines.push('  "tenant_id" UUID NOT NULL');
  lines.push('  "created_at" TIMESTAMPTZ DEFAULT now()');
  lines.push('  "updated_at" TIMESTAMPTZ DEFAULT now()');
  return `CREATE TABLE IF NOT EXISTS "${tableName}" (\n${lines.join(",\n")}\n);`;
}

function ThemePermissionPanel() {
  const { schema, updateTheme, updatePermissions } = useBuilderStore();
  const [permInput, setPermInput] = React.useState(`${schema.permissions.map((p) => `${p.role}:${p.resource}:${p.action}`).join("\n")}`);
  const [brandPacks, setBrandPacks] = React.useState<BrandPack[] | null>(null);

  // 品牌包列表：优先后端 API，失败降级内置 6 包（brand-pack-connect.loadBrandPacks）
  React.useEffect(() => {
    let cancelled = false;
    void loadBrandPacks().then((packs) => {
      if (!cancelled) setBrandPacks(packs);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const currentPack = brandPacks?.find((p) => p.brand_id === schema.theme.brandPackId) ?? null;

  // 一键应用：品牌包 tokens → Theme，经 builder-store updateTheme 写回 schema，
  // 保留 undo/redo 能力（store subscribe 自动 pushHistory）
  const handleApplyPack = (pack: BrandPack) => {
    updateTheme(applyBrandPackToTheme(pack, schema.theme));
  };

  return (
    <div className="space-y-6 p-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Palette className="h-4 w-4" />
            品牌包（一键主题）
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            选择一个品牌包，将设计令牌一键应用到当前应用主题（§5.5），可继续在下方主题设置中微调。
          </p>
          <BrandPackSelector value={currentPack} onChange={handleApplyPack} packs={brandPacks ?? undefined} />
          {currentPack && (
            <p className="text-xs text-muted-foreground">
              已应用品牌包：<span className="font-medium text-foreground">{currentPack.name}</span>
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Palette className="h-4 w-4" />
            主题
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>主色</Label>
              <Input
                type="color"
                data-testid="theme-primary-color"
                value={schema.theme.primaryColor || "#0f172a"}
                onChange={(e) => updateTheme({ primaryColor: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>圆角</Label>
              <Input
                value={schema.theme.borderRadius || "0.5rem"}
                onChange={(e) => updateTheme({ borderRadius: e.target.value })}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={schema.theme.darkMode ?? false}
              onCheckedChange={(v) => updateTheme({ darkMode: v })}
            />
            <Label>深色模式</Label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="h-4 w-4" />
            权限配置
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">每行格式：role:resource:action（action 支持 create/read/update/delete/*）</p>
          <Textarea
            value={permInput}
            onChange={(e) => {
              setPermInput(e.target.value);
              const perms = e.target.value
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean)
                .map((line) => {
                  const [role, resource, action] = line.split(":");
                  return { role: role || "user", resource: resource || "*", action: (action || "read") as "create" | "read" | "update" | "delete" | "*" };
                });
              updatePermissions(perms);
            }}
            rows={6}
            className="font-mono text-xs"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function PreviewPanel() {
  const { schema, activePageId } = useBuilderStore();
  const page = schema.pages.find((p) => p.id === activePageId);
  if (!page) return <div className="p-8 text-center">请选择页面</div>;
  return (
    <div className="h-full overflow-auto rounded-lg border bg-background p-4">
      <PageRenderer page={page} ctx={{ permissions: schema.permissions }} />
    </div>
  );
}

export function BuilderPage() {
  const {
    schema,
    setSchema,
    addChild,
    moveChild,
    findNodePath,
    previewMode,
    setPreviewMode,
    undo,
    redo,
    canUndo,
    canRedo,
    applyRemoteSchema,
  } = useBuilderStore();
  const [exportOpen, setExportOpen] = React.useState(false);
  const [aiPrompt, setAiPrompt] = React.useState("");
  const [aiLoading, setAiLoading] = React.useState(false);
  const [activeCanvas, setActiveCanvas] = React.useState<"canvas1" | "canvas2">("canvas1");
  const [savedAppId, setSavedAppId] = React.useState<string | null>(null);
  const [appStatus, setAppStatus] = React.useState<string | null>(null);
  const [versionHistoryOpen, setVersionHistoryOpen] = React.useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = React.useState(false);
  const [saveStatus, setSaveStatus] = React.useState("");
  const [appsList, setAppsList] = React.useState<AppRecord[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [clarifyOpen, setClarifyOpen] = React.useState(false);
  const [clarifyAnswers, setClarifyAnswers] = React.useState<Record<string, string>>({});
  const activePageId = useBuilderStore((state) => state.activePageId);
  const {
    connected: collabConnected,
    users: collabUsers,
    remoteSchema,
    remoteSchemaUser,
    sendCursor,
    clearRemoteSchema,
  } = useCollaboration(savedAppId);
  const applyingRemoteRef = React.useRef(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  React.useEffect(() => {
    void loadApps();
  }, []);

  // Load template prompt from URL query param
  React.useEffect(() => {
    const templateId = searchParams.get("template");
    if (!templateId) return;
    const template = appTemplates.find((t) => t.id === templateId);
    if (template) {
      setAiPrompt(template.prompt);
      setClarifyOpen(true);
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isUndo = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z" && !e.shiftKey;
      const isRedo =
        (e.metaKey || e.ctrlKey) && (e.key.toLowerCase() === "y" || (e.key.toLowerCase() === "z" && e.shiftKey));
      if (isUndo) {
        e.preventDefault();
        undo();
      } else if (isRedo) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [undo, redo]);

  const loadApps = async () => {
    try {
      const apps = await listApps();
      setAppsList(apps);
    } catch {
      setAppsList([]);
    }
  };

  // Dashboard / 模板安装跳转：/builder?app=<id> 自动加载对应应用
  React.useEffect(() => {
    const appId = searchParams.get("app");
    if (!appId || appsList.length === 0) return;
    const app = appsList.find((a) => a.id === appId);
    if (app) {
      setSchema(app.app_schema);
      setSavedAppId(app.id);
      setAppStatus(app.status);
      setSaveStatus(`已加载：${app.name}`);
      setSearchParams({}, { replace: true });
    }
  }, [appsList, searchParams, setSearchParams, setSchema]);

  const handleSave = async () => {
    setSaveStatus("保存中...");
    try {
      if (savedAppId) {
        const record = await updateApp(savedAppId, schema.name, schema);
        setAppStatus(record.status);
      } else {
        const record = await createApp(schema.name, schema);
        setSavedAppId(record.id);
        setAppStatus(record.status);
      }
      setSaveStatus("保存成功");
      await loadApps();
    } catch (error) {
      setSaveStatus(`保存失败：${String(error)}`);
    }
  };

  const handlePublish = async () => {
    if (!savedAppId) {
      setSaveStatus("请先保存应用，再执行发布");
      return;
    }
    setSaveStatus("发布中...");
    try {
      const record = await publishApp(savedAppId, schema.name, schema);
      setAppStatus(record.status);
      setSaveStatus(record.status === "active" ? "已发布" : "已保存并请求发布（等待后端确认状态）");
      await loadApps();
    } catch (error) {
      setSaveStatus(`发布失败：${String(error)}`);
    }
  };

  // 版本回滚成功：以恢复后的 schema 刷新编辑器（跳过 undo 历史），并刷新应用列表
  const handleVersionRestored = (detail: DebugSchemaDetail) => {
    setSchema(detail.app_schema, { skipHistory: true });
    setAppStatus(detail.status);
    setSaveStatus(`已恢复版本，当前版本 v${detail.current_version ?? "—"}`);
    void loadApps();
  };

  const handleLoad = (appId: string) => {
    const app = appsList.find((a) => a.id === appId);
    if (app) {
      setSchema(app.app_schema);
      setSavedAppId(app.id);
      setAppStatus(app.status);
      setSaveStatus("加载成功");
    }
  };

  const handleNew = () => {
    setSchema(createDefaultApp(schema.tenantId));
    setSavedAppId(null);
    setAppStatus(null);
    setSaveStatus("新建应用");
  };

  // AI 面板 / 自然语言画布调用 generate 新建应用后，记录 appId 以便后续保存/更新
  const handleAppCreated = React.useCallback((appId: string) => {
    setSavedAppId((prev) => prev ?? appId);
  }, []);

  const handleApplyRemote = () => {
    if (!remoteSchema) return;
    applyingRemoteRef.current = true;
    applyRemoteSchema(remoteSchema);
    clearRemoteSchema();
    window.setTimeout(() => {
      applyingRemoteRef.current = false;
    }, 50);
  };

  const handleMouseMove = React.useMemo(
    () =>
      throttle((event: React.MouseEvent<HTMLDivElement>) => {
        const rect = event.currentTarget.getBoundingClientRect();
        sendCursor({
          pageId: activePageId ?? undefined,
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        });
      }, 80),
    [sendCursor, activePageId],
  );

  const handleExport = async () => {
    if (!savedAppId) {
      // Fallback to client-side export for unsaved drafts.
      await downloadProjectZip(schema);
      return;
    }
    setSaveStatus("导出中...");
    try {
      await exportApp(savedAppId, schema.name || "debug-pavilion-app");
      setSaveStatus("导出成功");
    } catch (error) {
      setSaveStatus(`导出失败：${String(error)}`);
    }
  };

  const clarificationQuestions = [
    { key: "users", label: "主要使用者是谁？", placeholder: "例如：销售团队、客服人员、普通用户" },
    { key: "entities", label: "核心数据实体是什么？", placeholder: "例如：客户、订单、工单、文章" },
    { key: "features", label: "需要哪些核心功能？", placeholder: "例如：列表、搜索、新增、审批、AI 对话" },
    { key: "permissions", label: "是否需要权限区分？", placeholder: "例如：管理员可读写，普通用户只读" },
    { key: "special", label: "有什么特殊规则或字段？", placeholder: "例如：金额大于 10000 需总监审批" },
  ];

  const openClarifyDialog = () => {
    if (!aiPrompt.trim()) return;
    setClarifyOpen(true);
  };

  const handleGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setClarifyOpen(false);
    setAiLoading(true);
    setSaveStatus("AI 生成中...");

    const answersText = clarificationQuestions
      .filter((q) => clarifyAnswers[q.key]?.trim())
      .map((q) => `${q.label} ${clarifyAnswers[q.key]}`)
      .join("\n");

    const enrichedPrompt = answersText ? `${aiPrompt}\n\n补充信息：\n${answersText}` : aiPrompt;

    try {
      const result = await generateApp(enrichedPrompt);
      const generatedSchema = result.data as unknown as AppSchema;
      setSchema(generatedSchema);
      setAiPrompt("");
      setClarifyAnswers({});
      setSaveStatus("Schema 生成成功，正在应用数据模型...");

      if (generatedSchema.dataModels.length > 0) {
        try {
          let appId = savedAppId;
          if (!appId) {
            const record = await createApp(generatedSchema.name, generatedSchema);
            appId = record.id;
            setSavedAppId(appId);
          } else {
            await updateApp(appId, generatedSchema.name, generatedSchema);
          }
          const applyResult = await applyDataModels(appId, generatedSchema.dataModels);
          const errorCount = applyResult.errors.length;
          const appliedCount = applyResult.applied.length;
          if (errorCount > 0) {
            setSaveStatus(`数据模型应用：${appliedCount} 个成功，${errorCount} 个失败：${applyResult.errors.join("；")}`);
          } else {
            setSaveStatus(`AI 生成完成，已应用 ${appliedCount} 个数据模型。`);
          }
        } catch (applyError) {
          setSaveStatus(`Schema 生成成功，但数据模型应用失败：${String(applyError)}`);
        }
      } else {
        setSaveStatus("AI 生成完成，未包含数据模型。");
      }
    } catch (error) {
      setSaveStatus(`AI 生成失败：${String(error)}`);
      // eslint-disable-next-line no-console
      console.error("AI 生成失败", error);
    } finally {
      setAiLoading(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    // 从组件面板拖拽新材料
    if (activeId.startsWith("material__")) {
      const type = activeId.replace("material__", "");
      let targetId = overId;
      if (overId === CANVAS_DROP_ZONE_ID) {
        const page = schema.pages.find((p) => p.id === activePageId);
        targetId = page?.layout.id ?? overId;
      } else {
        const overPath = findNodePath(overId);
        if (overPath) {
          const overNode = overPath.node;
          const overMaterial = getMaterial(overNode.type);
          if (!(overMaterial?.allowChildren ?? false)) {
            // 放到非容器上时，尝试放到其父级末尾
            targetId = overPath.parent?.id ?? overId;
          }
        }
      }
      if (targetId) {
        addChild(targetId, type);
      }
      return;
    }

    // 画布内排序
    const activePath = findNodePath(activeId);
    const overPath = findNodePath(overId);
    if (!activePath || !overPath) return;
    if (activePath.parent?.id && activePath.parent.id === overPath.parent?.id) {
      const parentId = activePath.parent.id;
      const siblings = activePath.parent.children || [];
      const oldIndex = siblings.findIndex((c) => c.id === activeId);
      const newIndex = siblings.findIndex((c) => c.id === overId);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        moveChild(parentId, oldIndex, newIndex);
      }
    }
  };

  return (
    <div className="mx-auto flex h-[calc(100vh-7rem)] max-w-[1600px] flex-col gap-4">
      {/* Top toolbar */}
      <div className="flex flex-col gap-3 rounded-xl border bg-card/50 p-4 shadow-sm backdrop-blur-sm lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-md shadow-indigo-500/20">
            <Layers className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight">应用设计器</h1>
            <p className="text-xs text-muted-foreground">可视化搭建页面、数据模型与工作流</p>
          </div>
        </div>

        <div className="flex flex-1 items-center gap-2 lg:max-w-xl">
          <div className="relative flex-1">
            <Wand2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="描述你的应用，例如：创建一个客户管理系统，包含列表和新增表单"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  openClarifyDialog();
                }
              }}
              className="h-10 border-indigo-500/20 bg-background pl-10 pr-4 transition-shadow focus-visible:ring-indigo-500/30"
            />
          </div>
          <Button
            disabled={aiLoading || !aiPrompt.trim()}
            onClick={openClarifyDialog}
            className="h-10 bg-gradient-to-r from-indigo-500 to-violet-600 px-5 text-white shadow-md shadow-indigo-500/20 transition-all hover:from-indigo-400 hover:to-violet-500 hover:shadow-indigo-500/40"
          >
            {aiLoading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                生成中
              </span>
            ) : (
              <span className="flex items-center gap-1.5">
                <Sparkles className="h-4 w-4" />
                AI 生成
              </span>
            )}
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={schema.name}
            onChange={(e) => setSchema({ ...schema, name: e.target.value })}
            className="h-9 w-36 text-sm transition-colors focus-visible:ring-indigo-500/30"
            placeholder="应用名称"
          />
          <select
            value={savedAppId ?? ""}
            onChange={(e) => e.target.value && handleLoad(e.target.value)}
            className="h-9 rounded-md border bg-background px-2 text-xs transition-colors hover:border-indigo-500/30"
          >
            <option value="">加载应用...</option>
            {appsList.map((app) => (
              <option key={app.id} value={app.id}>
                {app.name}
              </option>
            ))}
          </select>
          <Button size="sm" onClick={() => void handleSave()} className="h-9 transition-transform active:scale-95">
            <Save className="mr-1.5 h-4 w-4" />
            保存
          </Button>
          <Button variant="outline" size="sm" disabled={!canUndo} onClick={undo} className="h-9 transition-transform active:scale-95">
            <Undo2 className="mr-1.5 h-4 w-4" />
            撤销
          </Button>
          <Button variant="outline" size="sm" disabled={!canRedo} onClick={redo} className="h-9 transition-transform active:scale-95">
            <Redo2 className="mr-1.5 h-4 w-4" />
            重做
          </Button>
          <Button variant="outline" size="sm" onClick={handleNew} className="h-9 transition-transform active:scale-95">
            新建
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPreviewMode(!previewMode)} className="h-9 transition-transform active:scale-95">
            <Eye className="mr-1.5 h-4 w-4" />
            {previewMode ? "退出预览" : "预览"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!savedAppId}
            onClick={() => setVersionHistoryOpen(true)}
            className="h-9 transition-transform active:scale-95"
          >
            <History className="mr-1.5 h-4 w-4" />
            版本历史
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!savedAppId}
            onClick={() => setPreviewDialogOpen(true)}
            className="h-9 transition-transform active:scale-95"
          >
            <Share2 className="mr-1.5 h-4 w-4" />
            分享预览
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!savedAppId}
            onClick={() => void handlePublish()}
            className="h-9 transition-transform active:scale-95"
          >
            <Rocket className="mr-1.5 h-4 w-4" />
            发布
          </Button>
          <Button variant="outline" size="sm" onClick={() => setExportOpen(!exportOpen)} className="h-9 transition-transform active:scale-95">
            <Code2 className="mr-1.5 h-4 w-4" />
            导出
          </Button>
        </div>
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-between gap-4 rounded-lg border bg-card/30 px-4 py-2 text-xs">
        <div className="flex items-center gap-3">
          {appStatus && (
            <span className="flex items-center gap-1.5" data-testid="app-status">
              {appStatus === "active" ? (
                <Badge className="gap-1 bg-green-600 hover:bg-green-600">
                  <Rocket className="h-3 w-3" />
                  已发布
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1">
                  草稿
                </Badge>
              )}
            </span>
          )}
          {saveStatus && (
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
              {saveStatus}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <DecrossPublishControl appId={savedAppId} appName={schema.name} />
          {collabConnected && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Users className="h-3.5 w-3.5" />
              <span>{collabUsers.length} 人在线</span>
              <div className="flex -space-x-1.5">
                {collabUsers.map((user) => (
                  <div
                    key={user.id}
                    className="h-5 w-5 rounded-full border-2 border-background shadow-sm"
                    style={{ backgroundColor: user.color }}
                    title={user.name}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {remoteSchema && (
        <div className="flex items-center justify-between gap-4 rounded-md border bg-amber-50 px-4 py-2 text-sm">
          <span className="text-amber-900">
            {remoteSchemaUser} 已更新 Schema，是否应用？
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={clearRemoteSchema}>
              忽略
            </Button>
            <Button size="sm" onClick={handleApplyRemote}>
              应用更新
            </Button>
          </div>
        </div>
      )}

      {exportOpen && (
        <div className="space-y-3 rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => void handleExport()}>
              <Code2 className="mr-1.5 h-4 w-4" />
              {savedAppId ? "后端导出 React 项目（ZIP）" : "本地导出 React 项目（ZIP）"}
            </Button>
          </div>
          <Textarea
            value={JSON.stringify(schema, null, 2)}
            readOnly
            rows={8}
            className="font-mono text-xs"
          />
        </div>
      )}

      <Tabs defaultValue="design" className="flex flex-1 flex-col overflow-hidden">
        <TabsList className="w-fit gap-1 rounded-xl border bg-card/50 p-1 backdrop-blur-sm">
          <TabsTrigger value="design" className="rounded-lg px-3 py-1.5 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-sm">
            <Layers className="mr-1.5 h-3.5 w-3.5" />
            设计
          </TabsTrigger>
          <TabsTrigger value="data" className="rounded-lg px-3 py-1.5 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-sm">
            <Database className="mr-1.5 h-3.5 w-3.5" />
            数据模型
          </TabsTrigger>
          <TabsTrigger value="data-view" className="rounded-lg px-3 py-1.5 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-sm">
            <Database className="mr-1.5 h-3.5 w-3.5" />
            数据视图
          </TabsTrigger>
          <TabsTrigger value="content" className="rounded-lg px-3 py-1.5 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-sm">
            <FileText className="mr-1.5 h-3.5 w-3.5" />
            内容管理
          </TabsTrigger>
          <TabsTrigger value="knowledge" className="rounded-lg px-3 py-1.5 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-sm">
            <BrainCircuit className="mr-1.5 h-3.5 w-3.5" />
            知识库
          </TabsTrigger>
          <TabsTrigger value="agents" className="rounded-lg px-3 py-1.5 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-sm">
            <Bot className="mr-1.5 h-3.5 w-3.5" />
            Agent 模板
          </TabsTrigger>
          <TabsTrigger value="deploy" className="rounded-lg px-3 py-1.5 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-sm">
            <Cloud className="mr-1.5 h-3.5 w-3.5" />
            部署与计费
          </TabsTrigger>
          <TabsTrigger value="workflows" className="rounded-lg px-3 py-1.5 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-sm">
            <Workflow className="mr-1.5 h-3.5 w-3.5" />
            工作流
          </TabsTrigger>
          <TabsTrigger value="theme" className="rounded-lg px-3 py-1.5 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-sm">
            <Palette className="mr-1.5 h-3.5 w-3.5" />
            主题 / 权限
          </TabsTrigger>
          <TabsTrigger value="files" className="rounded-lg px-3 py-1.5 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-sm">
            <FileIcon className="mr-1.5 h-3.5 w-3.5" />
            资源文件
          </TabsTrigger>
          <TabsTrigger value="audit" className="rounded-lg px-3 py-1.5 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-sm">
            <History className="mr-1.5 h-3.5 w-3.5" />
            审计
          </TabsTrigger>
          <TabsTrigger value="preview" className="rounded-lg px-3 py-1.5 text-xs data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-sm">
            <Eye className="mr-1.5 h-3.5 w-3.5" />
            实时预览
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="design"
          onMouseMove={handleMouseMove}
          className="relative flex flex-1 gap-4 overflow-hidden data-[state=inactive]:hidden"
        >
          {collabConnected &&
            collabUsers
              .filter((user) => user.cursor && user.cursor.pageId === activePageId)
              .map((user) => (
                <div
                  key={user.id}
                  className="pointer-events-none absolute z-50 flex items-center gap-1"
                  style={{ left: user.cursor!.x, top: user.cursor!.y }}
                >
                  <MousePointer className="h-4 w-4" style={{ color: user.color }} />
                  <span
                    className="rounded px-1 text-xs text-white"
                    style={{ backgroundColor: user.color }}
                  >
                    {user.name}
                  </span>
                </div>
              ))}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <Card className="flex w-64 flex-shrink-0 flex-col overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">资源面板</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-1 flex-col overflow-hidden py-0">
                <Tabs defaultValue="components" className="flex flex-1 flex-col overflow-hidden">
                  <TabsList className="w-full">
                    <TabsTrigger value="components" className="flex-1 text-xs">
                      组件
                    </TabsTrigger>
                    <TabsTrigger value="layers" className="flex-1 text-xs">
                      图层
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="components" className="flex-1 overflow-auto data-[state=inactive]:hidden">
                    <ComponentPanel />
                  </TabsContent>
                  <TabsContent value="layers" className="flex-1 overflow-hidden data-[state=inactive]:hidden">
                    <LayersPanel />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
            <Card className="flex flex-1 flex-col overflow-hidden">
              <PageTabs />
              <div className="flex items-center gap-1 border-b bg-card px-3 py-1.5">
                <button
                  type="button"
                  data-testid="canvas-tab-1"
                  onClick={() => setActiveCanvas("canvas1")}
                  className={cn(
                    "flex items-center gap-1 rounded-md px-2.5 py-1 text-xs transition-colors",
                    activeCanvas === "canvas1"
                      ? "bg-primary/10 font-semibold text-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <MousePointer className="h-3.5 w-3.5" />
                  画布① 无代码
                </button>
                <button
                  type="button"
                  data-testid="canvas-tab-2"
                  onClick={() => setActiveCanvas("canvas2")}
                  className={cn(
                    "flex items-center gap-1 rounded-md px-2.5 py-1 text-xs transition-colors",
                    activeCanvas === "canvas2"
                      ? "bg-primary/10 font-semibold text-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )}
                >
                  <MessageSquareText className="h-3.5 w-3.5" />
                  画布② 自然语言
                </button>
              </div>
              <CardContent className="flex-1 overflow-hidden p-0">
                {activeCanvas === "canvas1" ? (
                  <BuilderCanvas />
                ) : (
                  <NlCanvas savedAppId={savedAppId} onAppCreated={handleAppCreated} />
                )}
              </CardContent>
            </Card>
            <Card className="flex w-80 flex-shrink-0 flex-col overflow-hidden">
              <Tabs defaultValue="props" className="flex flex-1 flex-col overflow-hidden">
                <CardHeader className="pb-2">
                  <TabsList className="grid w-full grid-cols-2 gap-1">
                    <TabsTrigger value="props" className="text-xs">
                      属性
                    </TabsTrigger>
                    <TabsTrigger value="ai" className="text-xs">
                      AI
                    </TabsTrigger>
                  </TabsList>
                </CardHeader>
                <TabsContent value="props" className="min-h-0 flex-1 overflow-auto data-[state=inactive]:hidden">
                  <PropertyPanel />
                </TabsContent>
                <TabsContent value="ai" className="min-h-0 flex-1 overflow-hidden data-[state=inactive]:hidden">
                  <AiPanel savedAppId={savedAppId} onAppCreated={handleAppCreated} />
                </TabsContent>
              </Tabs>
            </Card>
          </DndContext>
        </TabsContent>

        <TabsContent value="data" className="flex-1 overflow-auto data-[state=inactive]:hidden">
          <DataModelPanel />
        </TabsContent>

        <TabsContent value="data-view" className="flex-1 overflow-hidden data-[state=inactive]:hidden">
          <DataViewPanel />
        </TabsContent>

        <TabsContent value="content" className="flex-1 overflow-hidden data-[state=inactive]:hidden">
          <ContentViewPanel />
        </TabsContent>

        <TabsContent value="knowledge" className="flex-1 overflow-hidden data-[state=inactive]:hidden">
          <KnowledgePanel />
        </TabsContent>

        <TabsContent value="agents" className="flex-1 overflow-hidden data-[state=inactive]:hidden">
          <AgentPanel />
        </TabsContent>

        <TabsContent value="deploy" className="flex-1 overflow-hidden data-[state=inactive]:hidden">
          <DeploymentPanel />
        </TabsContent>

        <TabsContent value="workflows" className="flex-1 overflow-auto data-[state=inactive]:hidden">
          <WorkflowPanel />
        </TabsContent>

        <TabsContent value="theme" className="flex-1 overflow-auto data-[state=inactive]:hidden">
          <ThemePermissionPanel />
        </TabsContent>

        <TabsContent value="files" className="flex-1 overflow-hidden data-[state=inactive]:hidden">
          <FileManager />
        </TabsContent>

        <TabsContent value="audit" className="flex-1 overflow-hidden data-[state=inactive]:hidden">
          <AuditLogPanel />
        </TabsContent>

        <TabsContent value="preview" className="flex-1 overflow-hidden data-[state=inactive]:hidden">
          <PreviewPanel />
        </TabsContent>
      </Tabs>

      {/* AI requirement clarification dialog */}
      <Dialog open={clarifyOpen} onOpenChange={setClarifyOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-600" />
              先和 AI 对齐需求
            </DialogTitle>
            <DialogDescription>
              回答几个简单问题，AI 会生成更贴合你需求的方案。不填的问题将自动推断。
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] space-y-4 overflow-y-auto py-2 pr-1">
            <div className="rounded-lg border bg-muted/50 p-3 text-sm text-muted-foreground">
              原始需求：{aiPrompt}
            </div>
            {clarificationQuestions.map((q) => (
              <div key={q.key} className="space-y-2">
                <Label htmlFor={`clarify-${q.key}`} className="text-sm font-medium">
                  {q.label}
                </Label>
                <Input
                  id={`clarify-${q.key}`}
                  placeholder={q.placeholder}
                  value={clarifyAnswers[q.key] ?? ""}
                  onChange={(e) => setClarifyAnswers((prev) => ({ ...prev, [q.key]: e.target.value }))}
                  className="border-indigo-500/20 focus-visible:ring-indigo-500/30"
                />
              </div>
            ))}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setClarifyOpen(false)}>
              取消
            </Button>
            <Button
              onClick={() => void handleGenerate()}
              disabled={aiLoading}
              className="bg-gradient-to-r from-indigo-500 to-violet-600 text-white"
            >
              {aiLoading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  生成中
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <Sparkles className="h-4 w-4" />
                  确认并生成
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 版本历史（T4.7：生命周期"版本管理"） */}
      <VersionHistoryDialog
        appId={savedAppId}
        open={versionHistoryOpen}
        onOpenChange={setVersionHistoryOpen}
        onRestored={handleVersionRestored}
      />

      {/* 分享预览（T4.7：生命周期"预览"） */}
      <PreviewDialog
        appId={savedAppId}
        appName={schema.name}
        open={previewDialogOpen}
        onOpenChange={setPreviewDialogOpen}
      />
    </div>
  );
}
